import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { useSchoolBootstrap } from "@/hooks/use-school-bootstrap";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type PlanPrice = {
  id: string;
  currency: string;
  amount: number;
  gateway: "razorpay" | "stripe";
};

type Plan = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  interval: "monthly" | "annual";
  includesAllCourses: boolean;
  prices: PlanPrice[];
  courseIds: string[];
};

type Props = {
  tenantSlug: string;
  plans: Plan[];
  isLoggedIn: boolean;
};

function formatAmount(amount: number, currency: string) {
  if (currency === "INR") return `₹${(amount / 100).toFixed(0)}`;
  return `$${(amount / 100).toFixed(2)}`;
}

export function SubscriptionPlansPanel({ tenantSlug, plans, isLoggedIn }: Props) {
  const t = useTranslations("subscriptions");
  const tRoot = useTranslations();
  const { tenant: school } = useSchoolBootstrap(tenantSlug);
  const navigate = useNavigate();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function startCheckout(plan: Plan, currency: "INR" | "USD") {
    if (!isLoggedIn) {
      navigate(`/t/${tenantSlug}/login`);
      return;
    }

    setLoadingPlanId(plan.id);
    setError(null);
    try {
      const res = await apiFetch("/v1/subscriptions/checkout/create", {
        method: "POST",
        body: JSON.stringify({ planId: plan.id, currency }),
      });
      if (!res.ok) {
        // PAY-10 (C4): the server's English message is never shown.
        setError(
          apiErrorText(await readApiErrorResponse(res), tRoot, "subscriptions.checkoutFailed", {
            namespace: "checkout.errors",
          }),
        );
        return;
      }
      const json = (await res.json()) as { data?: { redirectUrl: string } };
      if (!json.data?.redirectUrl) {
        setError(t("checkoutFailed"));
        return;
      }
      window.location.href = json.data.redirectUrl;
    } catch (err) {
      setError(
        apiErrorText(err, tRoot, "subscriptions.checkoutFailed", { namespace: "checkout.errors" }),
      );
    } finally {
      setLoadingPlanId(null);
    }
  }

  if (plans.length === 0) {
    return (
      <div
        className="rounded-xl border border-dashed border-[var(--brand-purple)]/20 bg-[var(--brand-surface)] p-8"
        data-testid="subscriptions-empty"
      >
        <p className="font-medium text-[var(--brand-heading)]">{t("empty")}</p>
        <p className="mt-2 text-sm text-[var(--brand-muted)]">
          {t("emptyHint", { school: school?.name ?? tenantSlug })}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to={`/t/${tenantSlug}/dashboard`}
            className="rounded-full px-4 py-2 text-sm font-semibold text-white"
            style={{ backgroundColor: "var(--accent-solid)" }}
            data-testid="subscriptions-go-dashboard"
          >
            {t("goToDashboard")}
          </Link>
          <Link
            to={`/t/${tenantSlug}/courses`}
            className="rounded-full border border-[var(--brand-purple)]/20 px-4 py-2 text-sm font-medium text-[var(--brand-heading)]"
            data-testid="subscriptions-browse-catalog"
          >
            {t("browseCatalog")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="subscription-plans">
      {plans.map((plan) => {
        const inr = plan.prices.find((price) => price.currency === "INR");
        const usd = plan.prices.find((price) => price.currency === "USD");
        return (
          <article
            key={plan.id}
            className="rounded-lg border border-[var(--border)] p-6"
            data-testid={`subscription-plan-${plan.slug}`}
          >
            <h2 className="text-xl font-semibold">{plan.title}</h2>
            {plan.description ? (
              <p className="mt-2 text-sm text-[var(--text-muted)]">{plan.description}</p>
            ) : null}
            <p className="mt-2 text-sm text-[var(--text-muted)]">
              {plan.interval === "monthly" ? t("intervalMonthly") : t("intervalAnnual")}
              {plan.includesAllCourses ? ` · ${t("allCoursesIncluded")}` : null}
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {inr ? (
                <button
                  type="button"
                  disabled={loadingPlanId === plan.id}
                  className="rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white hover:bg-[var(--accent-strong-hover)] disabled:opacity-60"
                  data-testid={`subscription-checkout-inr-${plan.slug}`}
                  onClick={() => startCheckout(plan, "INR")}
                >
                  {loadingPlanId === plan.id
                    ? t("redirecting")
                    : t("subscribeInr", { price: formatAmount(inr.amount, "INR") })}
                </button>
              ) : null}
              {usd ? (
                <button
                  type="button"
                  disabled={loadingPlanId === plan.id}
                  className="rounded-md border border-[var(--accent-border)] px-4 py-2 text-sm text-[var(--text-heading)] hover:bg-[var(--accent-soft)] disabled:opacity-60"
                  data-testid={`subscription-checkout-usd-${plan.slug}`}
                  onClick={() => startCheckout(plan, "USD")}
                >
                  {loadingPlanId === plan.id
                    ? t("redirecting")
                    : t("subscribeUsd", { price: formatAmount(usd.amount, "USD") })}
                </button>
              ) : null}
            </div>
          </article>
        );
      })}
      {error ? (
        <p className="text-sm text-red-600" data-testid="subscription-checkout-error">
          {error}
        </p>
      ) : null}
      {!isLoggedIn ? (
        <p className="text-sm text-[var(--text-muted)]">
          <Link to={`/t/${tenantSlug}/login`} className="underline">
            {t("signInToSubscribe")}
          </Link>
        </p>
      ) : null}
    </div>
  );
}

export function MockSubscriptionCheckoutPanel({
  tenantSlug,
  subscriptionPaymentId,
  provider,
}: {
  tenantSlug: string;
  subscriptionPaymentId: string;
  provider: string;
}) {
  const navigate = useNavigate();
  const t = useTranslations("subscriptions");
  const tCheckout = useTranslations("checkout");
  const tRoot = useTranslations();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function completePayment() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch("/v1/subscriptions/checkout/e2e-complete", {
        method: "POST",
        body: JSON.stringify({ subscriptionPaymentId }),
      });
      if (!res.ok) {
        setError(
          apiErrorText(await readApiErrorResponse(res), tRoot, "subscriptions.completionFailed", {
            namespace: "checkout.errors",
          }),
        );
        return;
      }
      navigate(`/t/${tenantSlug}/checkout/success?subscriptionPaymentId=${subscriptionPaymentId}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="mx-auto max-w-lg px-4 py-16" data-testid="subscription-checkout-mock">
      <h1 className="text-2xl font-semibold">{t("mockTitle")}</h1>
      <p className="mt-2 text-sm text-[var(--text-muted)]">
        {tCheckout("mockProvider")}{" "}
        <span data-testid="subscription-checkout-mock-provider">{provider}</span>
      </p>
      <button
        type="button"
        onClick={completePayment}
        disabled={loading}
        className="mt-8 rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white hover:bg-[var(--accent-strong-hover)] disabled:opacity-60"
        data-testid="subscription-checkout-mock-complete"
      >
        {loading ? tCheckout("mockProcessing") : t("mockComplete")}
      </button>
      {error ? (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {error}
        </p>
      ) : null}
    </main>
  );
}
